/*
 * Mexico-City week/date helpers (client side).
 *
 * The database anchors all "today"/week logic to America/Mexico_City via
 * app_today_mx() / app_week_end_mx(). These helpers mirror that on the client
 * so the UI (graying past days, building past-day options, expiry previews)
 * matches what RLS will actually enforce.
 *
 * Mexico City has had a FIXED UTC-6 offset since 2022 (no DST), so the absolute
 * boundary instant for week-overflow checks is computed with the literal
 * "-06:00" offset. The plain calendar helpers stay offset-agnostic by doing
 * weekday/arithmetic on UTC-noon dates.
 */

export const MX_TZ = 'America/Mexico_City';
export const MX_OFFSET = '-06:00';

/** Current calendar date in Mexico City as 'YYYY-MM-DD'. */
export function mxTodayStr() {
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: MX_TZ }).format(new Date());
}

/** Day of week for a 'YYYY-MM-DD' string: 0 = Sunday … 6 = Saturday. */
export function dowOfStr(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Add n days to a 'YYYY-MM-DD' string, returning a 'YYYY-MM-DD' string. */
export function addDaysStr(s, n) {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

/** Monday (week start) of the week containing the given 'YYYY-MM-DD'. */
export function mondayOfStr(s) {
  const dow = dowOfStr(s); // 0=Sun..6=Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  return addDaysStr(s, diff);
}

/** { todayStr, weekStartStr (Mon), weekEndStr (Sun) } for the current MX week. */
export function mxWeekBounds() {
  const todayStr = mxTodayStr();
  const weekStartStr = mondayOfStr(todayStr);
  const weekEndStr = addDaysStr(weekStartStr, 6);
  return { todayStr, weekStartStr, weekEndStr };
}

/**
 * Strictly-past days of the current MX week (week start … yesterday), as
 * 'YYYY-MM-DD' strings. Empty when today is Monday.
 */
export function pastDaysOfCurrentWeek() {
  const { todayStr, weekStartStr } = mxWeekBounds();
  const days = [];
  let d = weekStartStr;
  while (d < todayStr) {
    days.push(d);
    d = addDaysStr(d, 1);
  }
  return days;
}

/**
 * Absolute instant (Date) at which the current MX week ends — i.e. next
 * Monday 00:00 Mexico City. A grant whose expiry is at or after this instant
 * would leak into the next week and must be rejected.
 */
export function currentWeekEndBoundary() {
  const { weekStartStr } = mxWeekBounds();
  const nextMonday = addDaysStr(weekStartStr, 7);
  return new Date(`${nextMonday}T00:00:00${MX_OFFSET}`);
}

/** Format a 'YYYY-MM-DD' as e.g. "lunes 2 de junio". */
export function formatDayLabel(s) {
  return new Date(`${s}T12:00:00${MX_OFFSET}`).toLocaleDateString('es-MX', {
    timeZone: MX_TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** Format an instant as a Mexico-City wall-clock time, e.g. "16:35". */
export function formatMxTime(date) {
  return new Intl.DateTimeFormat('es-MX', {
    timeZone: MX_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}
